import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { usePieces } from '../hooks/usePieces';

function useReveal(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setVisible(true); obs.unobserve(el); }
    }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

export default function Collection() {
  const navigate = useNavigate();
  const [gridRef, gridVis] = useReveal(0.05);
  const { pieces } = usePieces();

  // Produit sélectionné — overlay plein écran, zéro changement de page
  const [expanding, setExpanding] = useState(null);
  const [selectedPiece, setSelectedPiece] = useState(null);
  const [detailPhase, setDetailPhase] = useState(0);
  const [selectedImageIdx, setSelectedImageIdx] = useState(0);

  const handlePieceClick = (piece, e) => {
    const img = e.currentTarget.querySelector('img');
    if (!img) return;
    const rect = img.getBoundingClientRect();
    setExpanding({ piece, rect: { top: rect.top, left: rect.left, width: rect.width, height: rect.height } });

    setTimeout(() => {
      setSelectedPiece(piece);
      setSelectedImageIdx(0);
      setDetailPhase(1);
      setExpanding(null);
      window.history.pushState({}, '', `/collection/${piece.id}`);
    }, 1200);
  };

  const handleCloseDetail = () => {
    setDetailPhase(0);
    setTimeout(() => {
      setSelectedPiece(null);
      window.history.pushState({}, '', '/collection');
    }, 400);
  };

  const [navKey, setNavKey] = useState(0); // force re-render des animations
  const [navDir, setNavDir] = useState('next'); // direction pour l'animation

  const handleNavPiece = (newPiece, dir) => {
    setNavDir(dir);
    setNavKey(k => k + 1);
    setSelectedPiece(newPiece);
    setSelectedImageIdx(0);
    window.history.replaceState({}, '', `/collection/${newPiece.id}`);
  };

  const handleImageChange = (idx) => {
    setSelectedImageIdx(idx);
    setNavKey(k => k + 1);
  };

  useEffect(() => {
    const onPopState = () => {
      if (selectedPiece) {
        setDetailPhase(0);
        setTimeout(() => setSelectedPiece(null), 400);
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [selectedPiece]);

  // Scroll-driven parallax sur la section intro
  const heroRef = useRef(null);
  const [sp, setSp] = useState(0);
  const [introMounted, setIntroMounted] = useState(false);

  // Layout du bento : 6 cellules scatterées dans une grille 8x5 (espace vide intentionnel)
  const bentoLayout = [
    { area: '1 / 2 / 3 / 5', wave1: 0, wave2: 6 },  // 2x3 top-left
    { area: '1 / 6 / 3 / 8', wave1: 1, wave2: 7 },  // 2x2 top-right
    { area: '3 / 1 / 4 / 3', wave1: 2, wave2: 8 },  // 1x2 middle-left
    { area: '3 / 4 / 5 / 7', wave1: 3, wave2: 9 },  // 2x3 center
    { area: '4 / 7 / 6 / 9', wave1: 4, wave2: 10 }, // 2x2 bottom-right
    { area: '5 / 2 / 6 / 4', wave1: 5, wave2: 11 }, // 1x2 bottom-left
  ];

  // Triggers de scroll random par cellule pour effet désorganisé
  const [cellTriggers] = useState(() =>
    bentoLayout.map(() => ({
      wave1: 0.50 + Math.random() * 0.08, // [0.50, 0.58]
      wave2: 0.78 + Math.random() * 0.08, // [0.78, 0.86]
    }))
  );


  const handleScroll = useCallback(() => {
    if (!heroRef.current) return;
    const rect = heroRef.current.getBoundingClientRect();
    const total = heroRef.current.offsetHeight - window.innerHeight;
    const progress = Math.max(0, Math.min(1, -rect.top / total));
    setSp(progress);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    window.addEventListener('scroll', handleScroll, { passive: true });
    const t = setTimeout(() => setIntroMounted(true), 100);
    return () => { window.removeEventListener('scroll', handleScroll); clearTimeout(t); };
  }, [handleScroll]);

  // Phases scroll :
  // 0.00 - 0.20 : Titre "Rêverie" visible seul
  // 0.15 - 0.30 : Titre fade out
  // 0.25 - 0.55 : Texte complet (2 paragraphes + tags) ensemble
  // 0.50 - 0.65 : Texte fade out
  // 0.60 - 1.00 : Aperçu images (grille dans le sticky)

  // Phase 1 : Titre Rêverie
  const titleOp = sp < 0.10 ? 1 : Math.max(0, 1 - (sp - 0.10) / 0.10);
  const titleY = sp * -60;

  // Phase 2 : Texte
  const textOp = (() => {
    if (sp < 0.22) return 0;
    if (sp < 0.30) return (sp - 0.22) / 0.08;
    if (sp < 0.40) return 1;
    if (sp < 0.46) return 1 - (sp - 0.40) / 0.06;
    return 0;
  })();
  const textY = (1 - textOp) * 20;

  // Phase 3 : Bento avec 2 vagues
  const imgOp = sp < 0.46 ? 0 : Math.min(1, (sp - 0.46) / 0.06);
  const imgScale = 0.94 + imgOp * 0.06;

  // Animation Wave 1 — entrée délicate, calme
  const getWave1Style = (trigger) => {
    const fadeIn = 0.05;
    const exitStart = 0.68;
    const exitEnd = 0.74;
    if (sp < trigger) return { opacity: 0, transform: 'scale(0.96) translateY(8px)', filter: 'blur(0px)' };
    if (sp < trigger + fadeIn) {
      const t = (sp - trigger) / fadeIn;
      const ease = 1 - Math.pow(1 - t, 2);
      return {
        opacity: ease,
        transform: `scale(${0.96 + ease * 0.04}) translateY(${8 - ease * 8}px)`,
        filter: `blur(0px)`,
      };
    }
    if (sp < exitStart) return { opacity: 1, transform: 'scale(1) translateY(0)', filter: 'blur(0)' };
    if (sp < exitEnd) {
      const t = (sp - exitStart) / (exitEnd - exitStart);
      const ease = 1 - Math.pow(1 - t, 2);
      return {
        opacity: 1 - ease,
        transform: `scale(${1 + ease * 0.02}) translateY(${-ease * 6}px)`,
        filter: `blur(0)`,
      };
    }
    return { opacity: 0, transform: 'scale(1.02) translateY(-6px)', filter: 'blur(0)' };
  };

  // Animation Wave 2 — émergence calme depuis le bas
  const getWave2Style = (trigger) => {
    const fadeIn = 0.06;
    if (sp < trigger) return { opacity: 0, transform: 'scale(0.97) translateY(12px)', filter: 'blur(0px)' };
    if (sp < trigger + fadeIn) {
      const t = (sp - trigger) / fadeIn;
      const ease = 1 - Math.pow(1 - t, 2);
      return {
        opacity: ease,
        transform: `scale(${0.97 + ease * 0.03}) translateY(${12 - ease * 12}px)`,
        filter: 'blur(0px)',
      };
    }
    return { opacity: 1, transform: 'scale(1) translateY(0)', filter: 'blur(0)' };
  };

  // Interactions : seulement quand chaque vague est stabilisée
  const wave1Interactive = sp >= 0.62 && sp < 0.68;
  const wave2Interactive = sp >= 0.92;

  // Moment de respiration entre les vagues (sp 0.74-0.78) — petit logo central
  const breathOp = (() => {
    if (sp < 0.73) return 0;
    if (sp < 0.755) return (sp - 0.73) / 0.025;
    if (sp < 0.78) return 1 - (sp - 0.755) / 0.025;
    return 0;
  })();

  return (
    <div>
      {/* ── EXPAND ANIMATION — image qui grandit ── */}
      {expanding && (
        <div className="fixed inset-0 z-[200] pointer-events-none">
          <div style={{
            position: 'absolute',
            top: expanding.rect.top,
            left: expanding.rect.left,
            width: expanding.rect.width,
            height: expanding.rect.height,
            backgroundImage: `url(${expanding.piece.img})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            animation: 'expandToFull 1.1s cubic-bezier(0.22, 1, 0.36, 1) forwards, dimImage 0.8s ease forwards 0.3s',
          }} />
          <div style={{
            position: 'absolute', inset: 0,
            boxShadow: 'inset 0 0 200px 80px rgba(0,0,0,0.5)',
            opacity: 0,
            animation: 'fadeIn 0.5s ease forwards 0.4s',
          }} />
          <style>{`
            @keyframes expandToFull { to { top:0; left:0; width:100vw; height:100vh; } }
            @keyframes dimImage { to { filter: brightness(0.4) contrast(1.1) saturate(0.85); } }
            @keyframes fadeIn { to { opacity:1; } }
          `}</style>
        </div>
      )}

      {/* ── PRODUCT DETAIL OVERLAY — rendu inline, pas de changement de page ── */}
      {selectedPiece && (() => {
        const p = selectedPiece;
        const show = detailPhase >= 1;
        const mainImg = p.images?.[selectedImageIdx] || p.images?.[0] || p.img;
        // Pièces voisines (wrap modulo length) : prev, next, next+1
        const len = pieces.length;
        const idx = pieces.findIndex(x => x.id === p.id);
        const neighbors = [
          { piece: pieces[(idx - 1 + len) % len], dir: 'prev' },
          { piece: pieces[(idx + 1) % len], dir: 'next' },
          { piece: pieces[(idx + 2) % len], dir: 'next' },
        ];
        // Direction du slide : next = texte vient de droite, prev = texte vient de gauche
        const slideFrom = navDir === 'next' ? 'translate-x-8' : '-translate-x-8';
        const slideFromY = navDir === 'next' ? 'translate-y-[-45%]' : 'translate-y-[-55%]';
        return (
          <div className={`fixed inset-0 z-[150] bg-black transition-opacity duration-300 ${show ? 'opacity-100' : 'opacity-0'}`}>
            {/* Image avec crossfade */}
            <img key={`img-${navKey}`} src={mainImg} alt={p.name}
              className="absolute inset-0 w-full h-full object-cover animate-imgReveal"
              style={{ filter: 'brightness(0.4) contrast(1.1) saturate(0.85)' }} />
            <div className="absolute inset-0" style={{ boxShadow: 'inset 0 0 200px 80px rgba(0,0,0,0.5)' }} />

            {/* Retour */}
            <button onClick={handleCloseDetail}
              className="absolute top-24 left-8 md:left-12 flex items-center gap-3 text-white/50 hover:text-white transition-all z-10 group duration-500">
              <span className="w-8 h-px bg-current transition-all duration-300 group-hover:w-14" />
              <span className="text-[11px] tracking-[0.2em] uppercase">Retour</span>
            </button>

            {/* Nom — slide depuis le côté */}
            <div key={`name-${navKey}`} className={`absolute top-1/4 left-8 md:left-12 lg:left-16 z-10 transition-all duration-[600ms] ease-out ${show ? 'opacity-100 translate-x-0' : `opacity-0 ${slideFrom}`}`}
              style={{ transitionDelay: '0.05s' }}>
              <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl font-light leading-none tracking-wide" style={{ color: p.name ? '#fff' : 'rgba(255,255,255,0.25)' }}>
                {p.name || 'Titre à définir'}
              </h1>
            </div>

            {/* Histoire — léger décalage */}
            <div key={`story-${navKey}`} className={`absolute top-1/2 left-8 md:left-12 lg:left-16 z-10 max-w-md transition-all duration-[600ms] ease-out ${show ? 'opacity-100 translate-y-[-50%]' : `opacity-0 ${slideFromY}`}`}
              style={{ marginTop: '60px', transitionDelay: '0.12s' }}>
              <p className="text-[15px] leading-[1.9] font-light" style={{ color: p.story ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.2)', fontStyle: p.story ? 'normal' : 'italic' }}>
                {p.story || 'Description à définir depuis l\'interface admin'}
              </p>
            </div>

            {/* Détails — slide up */}
            <div key={`details-${navKey}`} className={`absolute bottom-36 left-8 md:left-12 lg:left-16 z-10 transition-all duration-[600ms] ease-out ${show ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'}`}
              style={{ transitionDelay: '0.18s' }}>
              <div className="flex gap-10 flex-wrap">
                <div>
                  <p className="text-[9px] tracking-[0.3em] uppercase text-white/25 mb-1">Pierre</p>
                  <p className="font-serif text-lg text-white/80">{p.stone || '—'}</p>
                </div>
                <div>
                  <p className="text-[9px] tracking-[0.3em] uppercase text-white/25 mb-1">Origine</p>
                  <p className="font-serif text-lg text-white/80">{p.origin || '—'}</p>
                </div>
                <div>
                  <p className="text-[9px] tracking-[0.3em] uppercase text-white/25 mb-1">Taille</p>
                  <p className="font-serif text-lg text-white/80">{p.size || '—'}</p>
                </div>
                <div>
                  <p className="text-[9px] tracking-[0.3em] uppercase text-white/25 mb-1">Disponibilité</p>
                  <p className="font-serif text-lg text-white/80">
                    {p.available > 0 ? `${p.available} pièce${p.available > 1 ? 's' : ''}` : 'Sur commande'}
                  </p>
                </div>
              </div>
            </div>

            {/* CTA Nous écrire */}
            <div key={`cta-${navKey}`} className={`absolute bottom-10 left-8 md:left-12 lg:left-16 z-10 transition-all duration-[600ms] ease-out ${show ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}
              style={{ transitionDelay: '0.22s' }}>
              <Link to={`/contact?piece=${encodeURIComponent(p.name)}&pierre=${encodeURIComponent(p.stone || '')}`}
                className="inline-block px-8 py-3 border border-white/20 text-white/70 text-[11px] tracking-[0.25em] uppercase hover:bg-white hover:text-black transition-all duration-500">
                Nous écrire
              </Link>
            </div>

            {/* ── Carrousel miniatures images — bas centre ── */}
            {p.images?.length > 1 && (
              <div key={`thumbs-${navKey}`} className={`absolute bottom-16 left-1/2 -translate-x-1/2 z-10 transition-all duration-[600ms] ease-out ${show ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}
                style={{ transitionDelay: '0.20s' }}>
                <div className="flex gap-3">
                  {p.images.map((img, i) => {
                    const active = i === selectedImageIdx;
                    return (
                      <button key={i} onClick={() => handleImageChange(i)}
                        className="overflow-hidden border transition-all duration-300"
                        style={{
                          width: '56px', height: '56px',
                          borderColor: active ? '#C8A96E' : 'rgba(255,255,255,0.15)',
                          opacity: active ? 1 : 0.5,
                        }}>
                        <img src={img} alt=""
                          className="w-full h-full object-cover"
                          style={{ filter: active ? 'none' : 'brightness(0.7)' }} />
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── Pierres disponibles (pièces voisines) — bas droite ── */}
            <div key={`neighbors-${navKey}`} className={`absolute bottom-16 right-8 md:right-12 lg:right-16 z-10 transition-all duration-[600ms] ease-out ${show ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'}`}
              style={{ transitionDelay: '0.20s' }}>
              <p className="text-[10px] tracking-[0.3em] uppercase text-white/40 mb-3 text-right">
                Pierres disponibles
              </p>
              <div className="flex gap-3 justify-end">
                {neighbors.map(({ piece: n, dir }, i) => (
                  <button key={`${n.id}-${i}`} onClick={() => handleNavPiece(n, dir)}
                    className="group flex flex-col items-center gap-2 transition-all duration-300">
                    <div className="w-14 h-14 overflow-hidden border border-white/15 transition-all duration-300"
                      style={{ opacity: 0.7 }}>
                      <img src={n.img} alt={n.stone}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        style={{ filter: 'brightness(0.7)' }} />
                    </div>
                    <p className="text-[9px] tracking-[0.15em] uppercase text-white/40 group-hover:text-white/80 transition-colors duration-300">
                      {n.stone}
                    </p>
                  </button>
                ))}
              </div>
            </div>

            {/* Compteur */}
            <div key={`count-${navKey}`} className={`absolute bottom-4 left-1/2 -translate-x-1/2 z-10 transition-all duration-[600ms] ${show ? 'opacity-100' : 'opacity-0'}`}
              style={{ transitionDelay: '0.15s' }}>
              <p className="text-[10px] tracking-[0.4em] text-white/15 uppercase">
                {String(p.num || 0).padStart(2, '0')} / {String(pieces.length).padStart(2, '0')}
              </p>
            </div>

            <style>{`
              @keyframes imgReveal {
                from { opacity: 0; transform: scale(1.05); }
                to { opacity: 1; transform: scale(1); }
              }
              .animate-imgReveal {
                animation: imgReveal 0.7s cubic-bezier(0.22, 1, 0.36, 1) forwards;
              }
            `}</style>
          </div>
        );
      })()}

      {/* ── SCROLL-DRIVEN INTRO ── */}
      <section ref={heroRef} className="relative bg-black" style={{ height: '500vh' }}>
        <div className="sticky top-0 h-screen flex items-center justify-center px-6 overflow-hidden">

          {/* Image de fond (Phase 1 — disparait avec le scroll) */}
          <div className="absolute inset-0 pointer-events-none" style={{ opacity: titleOp }}>
            <img src="/photos/IMG_2717.jpeg" alt="Rêverie"
              className="w-full h-full object-cover"
              style={{ filter: 'brightness(0.45) contrast(1.1) saturate(0.85)' }} />
            <div className="absolute inset-0 bg-black/30" />
            <div className="absolute inset-0" style={{ boxShadow: 'inset 0 0 200px 80px rgba(0,0,0,0.5)' }} />
          </div>

          {/* Fond clair pour les phases 2 & 3 */}
          <div className="absolute inset-0 pointer-events-none bg-white" style={{ opacity: 1 - titleOp }} />

          {/* Phase 1 : Titre "Rêverie" */}
          <div className="absolute inset-0 flex items-center justify-center"
            style={{ opacity: titleOp, transform: `translateY(${titleY}px)` }}>
            <div className={`text-center transition-all duration-[1.5s] ease-out ${introMounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
              <div className="flex items-center justify-center gap-4 mb-8">
                <div className="w-10 h-px bg-white/30" />
                <div className="w-1.5 h-1.5 rotate-45 bg-white/50" />
                <div className="w-10 h-px bg-white/30" />
              </div>
              <h1 className="text-white italic font-light" style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: 'clamp(64px, 10vw, 128px)'
              }}>
                Rêverie
              </h1>
              <div className="w-12 h-px bg-white/30 mx-auto mt-8" />
            </div>
          </div>

          {/* Phase 2 : Texte complet (2 paragraphes) */}
          <div className="absolute inset-0 flex items-center justify-center px-6"
            style={{ opacity: textOp, transform: `translateY(${textY}px)` }}>
            <div className="text-center" style={{ maxWidth: '520px', fontFamily: "'Inter', sans-serif", fontSize: '18px', color: '#6B6B6B' }}>
              <p className="leading-[1.7] mb-8">
                <span className="italic" style={{ fontFamily: "'Playfair Display', serif" }}>Rêverie</span> est une collection d'instants suspendus.
                Elle évoque les moments où la pensée vagabonde...
              </p>
              <p className="leading-[1.7]">
                C'est à Bangkok, là où tout a commencé pour Pernety,
                que nous avons d'abord rêvé.
              </p>
            </div>
          </div>

          {/* Phase 3 + 4 : Le bento grid scatterée avec 2 vagues */}
          <div className="absolute inset-0 flex items-center justify-center px-6 md:px-12 pt-24 pb-24"
            style={{ opacity: imgOp, transform: `scale(${imgScale})` }}>
            <div ref={gridRef} className="w-full max-w-5xl mx-auto h-full max-h-[calc(100vh-200px)]">
              <div className="grid h-full gap-4 md:gap-6"
                style={{
                  gridTemplateColumns: 'repeat(8, 1fr)',
                  gridTemplateRows: 'repeat(5, 1fr)',
                }}>

                {bentoLayout.map((cell, i) => {
                  const trigs = cellTriggers[i];
                  const wave1Style = getWave1Style(trigs.wave1);
                  const wave2Style = getWave2Style(trigs.wave2);
                  const piece1 = pieces[cell.wave1];
                  const piece2 = pieces[cell.wave2];
                  return (
                    <div key={i} className="relative overflow-hidden" style={{ gridArea: cell.area }}>
                      {/* Wave 1 piece */}
                      <BentoOverlay piece={piece1} style={wave1Style} interactive={wave1Interactive} onClick={handlePieceClick} />
                      {/* Wave 2 piece */}
                      <BentoOverlay piece={piece2} style={wave2Style} interactive={wave2Interactive} onClick={handlePieceClick} />
                    </div>
                  );
                })}

              </div>
            </div>
          </div>

          {/* Moment de respiration — entre les 2 vagues */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none"
            style={{ opacity: breathOp, transition: 'opacity 0.3s ease-out' }}>
            <div className="text-center">
              <img src="/photos/Logo_fd_transparent.png" alt="" className="h-16 w-auto mx-auto opacity-30 mb-4" />
              <div className="w-px h-12 bg-gray-300 mx-auto" />
            </div>
          </div>
        </div>
      </section>

      {/* ── SUR COMMANDE ── */}
      <section className="py-20 md:py-28 px-8 md:px-16 lg:px-24 bg-white">
        <div className="max-w-3xl">
          <p style={{ fontFamily: "'Inter', sans-serif", color: '#6B6B6B', fontSize: '15px', lineHeight: '1.65' }} className="mb-3">
            Des pièces réalisées au gré des pierres choisies, sur commande.
          </p>
          <p style={{ fontFamily: "'Inter', sans-serif", color: '#6B6B6B', fontSize: '15px', lineHeight: '1.65' }}>
            Recherche de pierres sur demande.
          </p>
        </div>
      </section>
    </div>
  );
}

function BentoOverlay({ piece, style, interactive, onClick }) {
  if (!piece) return null;
  return (
    <div className={`group absolute inset-0 ${interactive ? 'cursor-pointer' : ''}`}
      style={{
        ...style,
        pointerEvents: interactive && style.opacity > 0.5 ? 'auto' : 'none',
        willChange: 'opacity, transform, filter',
      }}
      onClick={(e) => onClick(piece, e)}>

      <img src={piece.img} alt={piece.name}
        className="w-full h-full object-cover transition-transform duration-[1.2s] group-hover:scale-110"
        loading="lazy" />

      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-all duration-500 flex items-center justify-center">
        <p className="text-white font-serif text-base md:text-lg tracking-wider opacity-0 group-hover:opacity-100 translate-y-3 group-hover:translate-y-0 transition-all duration-500">
          {piece.name}
        </p>
      </div>
    </div>
  );
}
